<template>
    <img
        v-bind="$attrs"
        :src="url"
        :alt="alt"
        :referrerpolicy="referrerpolicy"
        :loading="loading"
        @error="onLoadError"
        v-if="url"
    />
</template>

<script lang="ts">
    import { defineComponent, type PropType } from 'vue';
    import { useSignedUrl } from '@/plugins/signedUrl/useSignedUrl.ts';
    import type { SignedUrlKind, SignedUrlResolvers } from '@/plugins/signedUrl/resolvers.ts';

    /**
     * Replacement for `<img>` whose source comes from a signed URL
     * endpoint. Fetches eagerly on mount, refetches before expiry,
     * and refetches once on a 403 to recover from races between the
     * cached value and the CloudFront edge.
     *
     * Renders nothing while the URL is in flight — wrap the component
     * with your own placeholder/skeleton if a visible loading state
     * is desired (e.g. a sibling `v-if="loading"` element).
     */
    export default defineComponent({
        name: 'SignedImg',
        inheritAttrs: false,
        props: {
            kind: {
                type: String as PropType<SignedUrlKind>,
                required: true,
            },
            params: {
                type: Array as PropType<Parameters<(typeof SignedUrlResolvers)[SignedUrlKind]>>,
                required: true,
            },
            alt: { type: String, default: '' },
            referrerpolicy: { type: String, default: 'origin' },
            loading: { type: String, default: undefined },
        },
        setup(props) {
            const { url, refresh } = useSignedUrl({
                kind: props.kind,
                params: () => props.params,
            });

            const onLoadError = async () => {
                // 403 from CloudFront usually means the cached URL just
                // expired. Force one refresh; if it still fails the
                // user-facing slot stays hidden.
                await refresh({ force: true }).catch(() => null);
            };

            return { url, onLoadError };
        },
    });
</script>
